import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';

import type { TaskInboxStatus } from '../queries';
import { sortOptionsForView } from '../lib/taskInboxSort';

export type TaskInboxView = 'list' | 'grouped';

const STATUS_OPTIONS: readonly TaskInboxStatus[] = ['todo', 'done', 'cancelled', 'all'];
const VIEW_OPTIONS: readonly TaskInboxView[] = ['list', 'grouped'];

interface TaskInboxControlsProps {
  readonly status: TaskInboxStatus;
  readonly onStatusChange: (status: TaskInboxStatus) => void;
  readonly view: TaskInboxView;
  readonly onViewChange: (view: TaskInboxView) => void;
  readonly sortId: string;
  readonly onSortChange: (sortId: string) => void;
}

/**
 * Toolbar for the My Cases inbox (PTV2-1321): status filter, list/by-patient
 * view toggle, and the sort selector. Sorting is server-side, so it only
 * applies to the paginated list view — it's hidden while grouping.
 */
export function TaskInboxControls({
  status,
  onStatusChange,
  view,
  onViewChange,
  sortId,
  onSortChange,
}: TaskInboxControlsProps): ReactElement {
  const { t } = useTranslation();
  const sortOptions = sortOptionsForView(view === 'grouped');

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <div className="flex items-center gap-2">
        <label htmlFor="my-cases-status" className="text-sm font-medium text-foreground">
          {t('routes.myCases.inbox.statusLabel')}
        </label>
        <Select
          id="my-cases-status"
          value={status}
          onChange={(e) => {
            onStatusChange(e.target.value as TaskInboxStatus);
          }}
          className="h-8 w-40"
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {t(`routes.myCases.inbox.status.${option}`)}
            </option>
          ))}
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <label htmlFor="my-cases-sort" className="text-sm font-medium text-foreground">
          {t('routes.myCases.inbox.sortLabel')}
        </label>
        <Select
          id="my-cases-sort"
          value={sortId}
          onChange={(e) => {
            onSortChange(e.target.value);
          }}
          className="h-8 w-56"
        >
          {sortOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {t(`routes.myCases.inbox.sort.${option.id}`)}
            </option>
          ))}
        </Select>
      </div>

      <div
        className="ml-auto inline-flex items-center rounded-md bg-muted p-0.5"
        role="group"
        aria-label={t('routes.myCases.inbox.viewLabel')}
      >
        {VIEW_OPTIONS.map((option) => (
          <Button
            key={option}
            type="button"
            size="sm"
            variant="ghost"
            aria-pressed={view === option}
            onClick={() => {
              onViewChange(option);
            }}
            className={cn(
              'h-7 px-3 text-xs hover:bg-transparent',
              view === option
                ? 'bg-white font-semibold text-foreground shadow-sm hover:bg-white dark:bg-card'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t(`routes.myCases.inbox.view.${option}`)}
          </Button>
        ))}
      </div>
    </div>
  );
}
